import * as dat from 'dat.gui';
import { SETTINGS_EVENTS } from '../../constants/Events';
/**
 *
 * @alias components.core.SettingsPlugin
 * @classdesc A Settings component allowing access to some standard items
 *
 * The settings available in a game may not use all the settings available in this componment, use what you need.
 *
 * ***This is a core jgg component, and so will be available to any game extending {@link AppBase}.***
 *
 * <pre>
 * this.game.settings
 * </pre>
 *
 * The default model has a few default settings pre-configured, which it can accomodate through its properties
 *
  <pre>
  {
    audio: true,
    vo: true,
    sfx: true,
    music: true,
    motion: true,
    captions: false,
  }</pre>
 *
 * If you require a different model or a different default starting state you can pass through your own in the [getConfig()]{@link getConfigBase} component options
 * <pre>
 * components : {
 *  settings : { data :  { ...settings } }
 * }
 * </pre>
 *
 * Or by calling the [setModel]{@link components.core.SettingsPlugin#setModel} method
 *
 * <pre>
 * this.game.settings.setModel(  { ...settings } )
 * </pre>
 *
 * If you are using any properties in your model not covered by the plugin properties, you can access them via the [getSettingValue]{@link components.core.SettingsPlugin#getSettingValue} and [setSettingValue]{@link components.core.SettingsPlugin#setSettingValue} methods.
 *
 * <pre>
 * game.settings.getSettingValue('property')
 * game.settings.setSettingValue('property')
 * </pre>
 *
 * If you are not using {@link AppBase} and wish to use this component, you can install it as a [Phaser Global Plugin]{@link https://photonstorm.github.io/phaser3-docs/Phaser.Plugins.PluginManager.html}
 * <pre>
 * game.plugins.install('key', SettingsPlugin, true, 'scenekey' || null, { ...settings } );
 * </pre>
 *
 */
class SettingsPlugin extends Phaser.Plugins.BasePlugin {
  constructor(scene, pluginManager) {
    super(scene, pluginManager);
    this._model = {
      audio: true,
      vo: true,
      sfx: true,
      music: true,
      motion: true,
      captions: false,
    };
    this._events = new Phaser.Events.EventEmitter();
  }

  init(config) {
    if (config) {
      this._model = { ...config };
    }
  }

  onGameReady() {
    this._loadSettings();
  }

  /**
   * Change the settings model if you need more options that supported
   * @param {Object} model The settings model you want to use
   */
  setModel(model) {
    this._model = model;
    this._loadSettings();
  }

  /**
   * The event emitter for this component.
   * @type {Phaser.Events.EventEmitter}
   */
  get events() {
    return this._events;
  }

  /**
   * Enable Audio
   * @type {bool}
   * @default true
   */
  get audio() {
    return this.getSettingValue('audio');
  }
  set audio(value) {
    this.setSettingValue('audio', value);
    this.events.emit(SETTINGS_EVENTS.AUDIO_CHANGED);
  }

  /**
   * Toggle audio on/off
   */

  toggleAudio() {
    this.audio = this.audio === false;
  }

  /**
   * Enable Voiceover
   * @type {bool}
   * @default true
   */
  get vo() {
    return this.getSettingValue('vo');
  }
  set vo(value) {
    this.setSettingValue('vo', value);
    this._events.emit(SETTINGS_EVENTS.AUDIO_CHANGED);
  }
  /**
   * Enable sfx
   * @type {bool}
   * @default true
   */
  get sfx() {
    return this.getSettingValue('sfx');
  }
  set sfx(value) {
    this.setSettingValue('sfx', value);
    this.events.emit(SETTINGS_EVENTS.AUDIO_CHANGED);
  }
  /**
   * Enable music
   * @type {bool}
   * @default true
   */
  get music() {
    return this.getSettingValue('music');
  }
  set music(value) {
    this.setSettingValue('music', value);
    this.events.emit(SETTINGS_EVENTS.AUDIO_CHANGED);
  }
  /**
   * Enable Background animations
   * @type {bool}
   * @default true
   */
  get motion() {
    return this.getSettingValue('motion');
  }
  set motion(value) {
    this.setSettingValue('motion', value);
  }
  /**
   * Enable Captions
   * @type {bool}
   * @default false
   */
  get captions() {
    return this.getSettingValue('captions');
  }
  set captions(value) {
    this.setSettingValue('captions', value);
  }

  /**
   * Toggle a setting value, returns result
   * @param {string} settingid The settings to toggle
   * @returns {bool} The updated settings value
   */
  toggleSetting(settingid) {
    const curr = this.getSettingValue(settingid);
    const result = curr === false;
    this.setSettingValue(settingid, result);
    return result;
  }

  /**
   * Get the value of a specific setting property
   * @param {string} settingid
   * @returns {*} the value of the setting
   */
  getSettingValue(settingid) {
    return this._model[settingid];
  }

  /**
   * Set the value of a specific setting property
   * @param {string} settingid name of property to set
   * @param {*} value value to set
   */
  setSettingValue(settingid, value) {
    this._model[settingid] = value;
    this._events.emit(SETTINGS_EVENTS.CHANGED, { setting: settingid, value });
    this._saveSettings();
  }

  /**
   * Get all the settings in one object
   * @returns {Object} All settings
   */
  getAllSettings() {
    return this._model;
  }

  /**
   * Show settings
   *
   * Note - By default JGG does not have a settings display as our standalone games don't usually require them
   * This method is mainly here for frameworks/games to overwrite in an extended class should they need a settings screen.
   *
   */
  showSettings() {
    this._createTempGUI();
    this.gui.open();
    this.gui.show();
  }

  /**
   * Close settings
   *
   * Note - By default JGG does not have a settings display as our standalone games don't usually require them
   * This method is mainly here for frameworks/games to overwrite in an extended class should they need a settings screen.
   *
   */
  closeSettings() {
    this.gui.close();
    this.gui.hide();
    this.onSettingsClosed();
  }

  /**
   * On settings closed
   */
  onSettingsClosed() {
    this.events.emit(SETTINGS_EVENTS.CLOSED);
  }

  _createTempGUI() {
    if (!this.gui) {
      this.gui = new dat.GUI({
        name: 'Settings',
        closed: false,
        autoPlace: false,
        closeOnTop: false,
      });
      this.gui.domElement.addEventListener('click', () => {
        if (this.gui.closed) {
          this.closeSettings();
        }
      });
      this.gui.domElement.id = 'settings-gui';
      this.gui.domElement.style.position = 'absolute';
      this.gui.domElement.style.top = 0;
      this.gui.domElement.style.right = 0;
      this.game.scale.parent.appendChild(this.gui.domElement);
      Object.keys(this._model).forEach((key) => {
        this.gui
          .add(this._model, key)
          .name(key)
          .onChange((e) => {
            if (key === 'audio') this.audio = e;
            else this.setSettingValue(key, e);
          });
      });
    }
  }

  _saveSettings() {
    try {
      localStorage.setItem('jggsettings', JSON.stringify(this._model));
    } catch (e) {
      console.log('no local storage');
    }
  }

  _loadSettings() {
    try {
      let stored = localStorage.getItem('jggsettings');
      if (stored) {
        stored = JSON.parse(stored);
        Object.keys(this._model).forEach((key) => {
          if (stored.hasOwnProperty(key)) {
            this._model[key] = stored[key];
          }
        });
      }
    } catch (e) {
      console.log('no local storage');
    }
  }

  destroy() {
    this._events.destroy();
    this._events = null;
    this._model = null;
  }
}

export { SettingsPlugin };
